import { AlertTriangle } from 'lucide-react';

export const formatCost = (value) => `$${Number(value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
export const formatCount = (value) => Number(value ?? 0).toLocaleString('en-US');

const Tile = ({ label, value, hint }) => (
  <div className="min-w-0 rounded-xl border border-solid border-border bg-card p-4 shadow-sm">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
    {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);

const Breakdown = ({ title, rows, emptyLabel }) => (
  <div className="min-w-0 rounded-xl border border-solid border-border bg-card p-4 shadow-sm">
    <h3 className="mb-3 text-sm font-semibold text-foreground">{title}</h3>
    {rows.length === 0 ? (
      <p className="text-sm text-muted-foreground">{emptyLabel}</p>
    ) : (
      <ul className="text-sm">
        {rows.map((row) => (
          <li key={`${row.firmId ?? ''}${row.key}`} className="flex items-baseline gap-3 border-b border-solid border-border py-1.5 last:border-b-0">
            <span className="min-w-0 flex-1 truncate text-foreground" title={row.key || 'Untracked'}>{row.key || 'Untracked'}</span>
            <span className="shrink-0 tabular-nums text-muted-foreground">{formatCount(row.operations)}</span>
            <span className="w-24 shrink-0 text-right font-medium tabular-nums text-foreground">{formatCost(row.cost)}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const UsageSummary = ({ summary }) => {
  if (!summary) return null;
  const { totals, byFeature, byModel, byFirm, unpriced } = summary;
  const averageCost = totals.operations ? totals.cost / totals.operations : 0;

  return (
    <div className="mb-6 min-w-0 space-y-4">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 2xl:grid-cols-6">
        <Tile label="Total cost" value={formatCost(totals.cost)} />
        <Tile label="Operations" value={formatCount(totals.operations)} hint={`${formatCost(averageCost)} each on average`} />
        <Tile label="Input tokens" value={formatCount(totals.inputTokens)} />
        <Tile label="Output tokens" value={formatCount(totals.outputTokens)} />
        <Tile label="Firms" value={formatCount(totals.uniqueFirms)} />
        <Tile label="Users" value={formatCount(totals.uniqueUsers)} />
      </div>

      {unpriced.length > 0 ? (
        <div className="flex min-w-0 items-start gap-3 rounded-xl border border-solid border-destructive/40 bg-destructive/5 p-4 text-sm text-foreground">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
          <p>
            These calls used tokens but have no price, so their cost is counted as $0:{' '}
            {unpriced.map((item) => `${item.model || 'unknown model'} (${formatCount(item.operations)})`).join(', ')}.
          </p>
        </div>
      ) : null}

      {totals.unmeasured > 0 ? (
        <div className="flex items-start gap-3 rounded-xl border border-solid border-border bg-muted p-4 text-sm text-foreground">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <p>
            {formatCount(totals.unmeasured)} AI text calls were recorded with no token count, usually because the provider never reported
            usage (for example a stopped generation). Their cost is under-counted.
          </p>
        </div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-3">
        <Breakdown title="Top features by cost" rows={byFeature} emptyLabel="No usage in this range." />
        <Breakdown title="Top models by cost" rows={byModel} emptyLabel="No usage in this range." />
        <Breakdown title="Top firms by cost" rows={byFirm} emptyLabel="No usage in this range." />
      </div>
    </div>
  );
};

export default UsageSummary;
